import {
    BackgroundColor,
    type ColorParams,
    MultiPageImageModel,
    MultiPageVideoModel,
    TextElement,
    type VisualElement,
} from '@bynder-studio/render-core';
import { clone, equals, path } from 'rambda';
import { Gradient, SolidColor } from '@bynder-studio/misc';
import { isGradientColor } from 'packages/pages/editor/RightSideMenu/FormComponents/ColorPicker/utils';
import { BrandColor, BrandColorPalette, GradientBrandColor, SolidBrandColor } from 'packages/store/brandColors/types';
import { hexToRgb, rgbaToObject } from 'packages/common/editor/editorHelper';

type ElementColorProperties = { colorProperty: string; elementPropertyToUpdate: string };
type ElementColor = [ElementColorProperties, ColorParams];

const GRADIENT_INCOMPATIBLE_PROPERTIES = ['dropShadow'];

// Support only 2 levels of nesting. If you want more think about some sort of recursion or lodash _.set() alternative
const elementColorProperties: ElementColorProperties[] = [
    { colorProperty: '', elementPropertyToUpdate: 'color' }, // is case of BackgroundColor element
    { colorProperty: '', elementPropertyToUpdate: 'fillColor' },
    { colorProperty: '', elementPropertyToUpdate: 'borderColor' },
    { colorProperty: 'color', elementPropertyToUpdate: 'dropShadow' },
    { colorProperty: 'color', elementPropertyToUpdate: 'textBackground' },
    { colorProperty: 'borderColor', elementPropertyToUpdate: 'textBackground' },
];

// get all color properties from element with brand color attached
export const getElementColors = (element: VisualElement | BackgroundColor): ElementColor[] =>
    elementColorProperties.reduce((acc, properties) => {
        const { colorProperty, elementPropertyToUpdate } = properties;
        const colorPath = [elementPropertyToUpdate, colorProperty].filter((prop) => !!prop).join('.');
        const color: ColorParams | undefined = path(colorPath, element);

        if (color && color.brandColorId) {
            return [...acc, [properties, color]];
        }

        return acc;
    }, [] as ElementColor[]);

export const findBrandColorById = (id: number, palettes: BrandColorPalette[]): BrandColor | undefined =>
    palettes
        .reduce((acc, palette) => (palette.brandColors ? [...acc, ...palette.brandColors] : acc), [] as BrandColor[])
        .find((brandColor) => brandColor.id === id);

// Creates a new color object (solid or gradient) based on the brandColor type
export const createNewElementColor = (brandColor: BrandColor, elementColor: ColorParams): ColorParams => {
    if (isGradientColor(brandColor)) {
        const bc = brandColor as GradientBrandColor;

        return {
            type: bc.type,
            angle: bc.angle,
            stops: bc.stops,
            brandColorId: elementColor.brandColorId,
        };
    }

    const bc = brandColor as SolidBrandColor;

    const rgb = rgbaToObject(hexToRgb(bc.hexCode, bc.alpha));

    return {
        ...rgb,
        brandColorId: elementColor.brandColorId,
    };
};

export const updateTextColors = (
    formattedText: TextElement['formattedText'],
    palettes: BrandColorPalette[],
): TextElement['formattedText'] => {
    const { runs } = formattedText;

    return {
        ...formattedText,
        runs: runs.map((item) => {
            const run = clone(item);
            const brandColorId = run.color.brandColorId;

            if (brandColorId) {
                const brandColor = findBrandColorById(brandColorId, palettes);

                if (brandColor) {
                    if (brandColor.deleted) {
                        // Sync to latest color and detach the brandColorId
                        const updatedColor = matchColors(run.color, brandColor);

                        run.color = updatedColor
                            ? { ...updatedColor, brandColorId: null }
                            : { ...run.color, brandColorId: null };
                    } else {
                        run.color = createNewElementColor(brandColor, {
                            ...run.color,
                            brandColorId,
                        } as ColorParams);
                    }
                } else {
                    // Brand color not found, remove the reference
                    run.color = { ...run.color, brandColorId: null };
                }
            }

            return run;
        }),
    };
};

// find difference between element color and brand color. If there is no diff return nothing
export const matchColors = (elementColor: ColorParams, brandColor: BrandColor) => {
    if (isGradientColor(brandColor)) {
        const bc = brandColor as GradientBrandColor;
        const elColor = { ...elementColor } as Gradient;

        return equals(
            { type: bc.type, stops: bc.stops, angle: bc.angle },
            { type: elColor.type, stops: elColor.stops, angle: elColor.angle },
        )
            ? null
            : {
                  type: bc.type,
                  angle: bc.angle,
                  stops: bc.stops,
                  brandColorId: bc.id,
              };
    }

    const { red, green, blue, opacity } = elementColor as SolidColor;
    const elementColorRGBA = `rgba(${red},${green},${blue},${opacity})`;

    const bc = brandColor as SolidBrandColor;
    const brandColorRGBA = hexToRgb(bc.hexCode, bc.alpha);

    return brandColorRGBA === elementColorRGBA ? null : createNewElementColor(brandColor, elementColor);
};

// Support only 2 levels of nesting. If you want more think about some sort of recursion or lodash _.set() alternative
export const updateElementColor = (
    acc: any,
    properties: ElementColorProperties,
    element: any,
    elementColorToBeUpdated: any,
) => {
    if (properties.colorProperty) {
        acc[properties.elementPropertyToUpdate] = {
            ...(acc[properties.elementPropertyToUpdate] ?? element[properties.elementPropertyToUpdate]),
            [properties.colorProperty]: { ...elementColorToBeUpdated },
        };
    } else {
        acc[properties.elementPropertyToUpdate] = { ...elementColorToBeUpdated };
    }

    return acc;
};

// Match and update all color properties on every pageto up-to-date brand colors
export const syncBrandColors = (
    creativeModel: MultiPageVideoModel | MultiPageImageModel,
    palettes: BrandColorPalette[],
    save: () => void,
) => {
    const originalPageIndex = creativeModel.getCurrentPageIndex();
    const pageCount = creativeModel.getModels().length;
    let isChanged = false;

    // Iterate through all pages
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
        creativeModel.setCurrentPageIndex(pageIndex);

        [
            creativeModel.getBackgroundColor(),
            ...creativeModel.getAllElementsRecursively<VisualElement>('visual'),
        ].forEach((element) => {
            const elementColors = getElementColors(element);

            const elementColorsToBeUpdated = elementColors.reduce(
                (acc, elementColor) => {
                    const [properties, elementColorObj] = elementColor;
                    const brandColor = findBrandColorById(elementColorObj.brandColorId as number, palettes);

                    if (!brandColor) {
                        return updateElementColor(acc, properties, element, { ...elementColorObj, brandColorId: null });
                    }

                    // Gradients are not supported for certain properties (e.g. dropShadow).
                    // If the brand color was changed to a gradient, detach it and keep the original color.
                    if (
                        isGradientColor(brandColor) &&
                        GRADIENT_INCOMPATIBLE_PROPERTIES.includes(properties.elementPropertyToUpdate)
                    ) {
                        return updateElementColor(acc, properties, element, { ...elementColorObj, brandColorId: null });
                    }

                    const elementColorToBeUpdated = matchColors(elementColorObj, brandColor);

                    if (elementColorToBeUpdated) {
                        const colorToApply = elementColorToBeUpdated || elementColorObj;
                        const colorParams = {
                            ...colorToApply,
                        };

                        if (brandColor.deleted) {
                            colorParams.brandColorId = null;
                        }

                        return updateElementColor(acc, properties, element, colorParams);
                    }

                    return acc;
                },
                {} as Record<string, any>,
            );

            if (element instanceof TextElement) {
                const formattedText = element.getTextProps();
                const newFormattedText = updateTextColors(formattedText, palettes);

                if (!equals(newFormattedText, formattedText)) {
                    elementColorsToBeUpdated.formattedText = newFormattedText;
                }
            }

            // update element only if it is necessary
            if (Object.keys(elementColorsToBeUpdated).length) {
                isChanged = true;

                if (element instanceof BackgroundColor) {
                    creativeModel.updateGlobalProperty('BACKGROUND_COLOR', elementColorsToBeUpdated);
                } else {
                    creativeModel.updateElement(element.id, elementColorsToBeUpdated);
                }
            }
        });
    }

    // Restore original page
    creativeModel.setCurrentPageIndex(originalPageIndex);

    if (isChanged) {
        save();
    }
};
